import React, { useState } from 'react'
import { Loader2, ChevronRight } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import PageHeader from '@/components/PageHeader'
import { formatDate } from '@/lib/utils'

const dummyInbodyMeasurement = {
  WT: 185.5,
  SMM: 92.4,
  BFM: 28.2,
  BMI: 24.1,
  PBF: 15.2,
  WED: 0.385,
  ICW: 62.4,
  ECW: 38.2,
  DM: 42.5,
  TBW: 100.6,
  FFM: 157.3,
  LRA: 8.2,
  LLA: 8.1,
  LT: 52.4,
  LLL: 22.1,
  LRL: 22.3,
  NAME: 'John Doe',
  HT: "6'1\"",
  AGE: '28',
  GENDER: 'M',
  BMR: '1950 kcal',
  VFALevel: '8',
}

const dummyInbodyDates = [
  '2024-03-15T10:30:00',
  '2024-02-10T09:15:00',
  '2024-01-05T11:00:00',
]

const formatInbodyDateTime = (dateStr) => formatDate(dateStr, { fallback: '-' })

const formatNumberSmart = (val) => {
  if (val === undefined || val === null) return '-'
  const num = Number(val)
  return isNaN(num) ? val : num.toLocaleString()
}

const clampPercent = (val) => Math.min(Math.max(val, 0), 100)

const ClientResources = () => {
  const [inbodySelectedDate, setInbodySelectedDate] = useState(dummyInbodyDates[0])
  const [inbodyLoading, setInbodyLoading] = useState(false)
  const inbodyMeasurement = dummyInbodyMeasurement
  const inbodyDates = dummyInbodyDates
  const inbodyError = null

  const fetchInbodyData = () => {
    setInbodyLoading(true)
    setTimeout(() => setInbodyLoading(false), 800)
  }

  const handleInbodyDateSelect = (date) => {
    setInbodySelectedDate(date)
  }

  const panelTitle = (text) => (
    <div className="border-b border-slate-200 px-4 py-3">
      <h2 className="text-[15px] font-semibold text-slate-900">{text}</h2>
    </div>
  )

  const meters = [
    { label: 'Muscle-fat balance', metric: 'SMM', value: inbodyMeasurement.SMM, target: 120 },
    { label: 'Obesity analysis', metric: 'BMI', value: inbodyMeasurement.BMI, target: 45 },
    { label: 'Hydration balance', metric: 'ECW/TBW', value: inbodyMeasurement.WED, range: [0.3, 0.5] },
  ]

  return (
    <div className="app-page pb-12">
      <PageHeader
        title="InBody Studio"
        subtitle={inbodySelectedDate ? `Scan: ${formatInbodyDateTime(inbodySelectedDate)}` : 'Body composition canvas'}
      >
        <Button variant="outline" size="sm" onClick={fetchInbodyData} disabled={inbodyLoading}>
          {inbodyLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Sync latest scan
        </Button>
      </PageHeader>

      {inbodyDates.length > 0 && (
        <div className="flex gap-2 overflow-x-auto" role="group" aria-label="Scan dates">
          {inbodyDates.map((date) => {
            const selected = date === inbodySelectedDate
            return (
              <button
                key={date}
                type="button"
                aria-pressed={selected}
                onClick={() => handleInbodyDateSelect(date)}
                className={`whitespace-nowrap rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                  selected
                    ? 'border-primary bg-accent text-blue-800'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-100'
                }`}
              >
                {formatInbodyDateTime(date)}
              </button>
            )
          })}
        </div>
      )}

      {/* Core metrics */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        {[
          { label: 'Weight', value: inbodyMeasurement.WT, unit: 'lbs' },
          { label: 'SMM', value: inbodyMeasurement.SMM, unit: 'lbs' },
          { label: 'Body fat', value: inbodyMeasurement.BFM, unit: 'lbs' },
          { label: 'BMI', value: inbodyMeasurement.BMI, unit: 'kg/m²' },
          { label: 'PBF', value: inbodyMeasurement.PBF, unit: '%' },
          { label: 'Hydration', value: inbodyMeasurement.WED, unit: 'ECW/TBW' },
        ].map((metric) => (
          <Card key={metric.label} className="p-4">
            <p className="text-xs font-semibold text-slate-500">{metric.label}</p>
            <p className="mt-1 whitespace-nowrap">
              <span className="text-xl font-semibold tracking-[-0.01em] text-slate-900">{formatNumberSmart(metric.value)}</span>
              <span className="ml-1 text-xs text-slate-500">{metric.unit}</span>
            </p>
          </Card>
        ))}
      </div>

      {/* Analysis */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="overflow-hidden">
          {panelTitle('Composition analysis')}
          <ul>
            {[
              ['Intercellular water', inbodyMeasurement.ICW, 'lbs'],
              ['Extracellular water', inbodyMeasurement.ECW, 'lbs'],
              ['Dry lean mass', inbodyMeasurement.DM, 'lbs'],
              ['Body fat mass', inbodyMeasurement.BFM, 'lbs'],
            ].map(([label, value, unit]) => (
              <li key={label} className="flex items-center justify-between border-b border-slate-100 px-4 py-3 text-sm last:border-b-0 hover:bg-background">
                <span className="text-slate-700">{label}</span>
                <span className="text-slate-900">
                  <span className="font-semibold">{formatNumberSmart(value)}</span>
                  <span className="ml-1 text-xs text-slate-500">{unit}</span>
                </span>
              </li>
            ))}
          </ul>
          <dl className="grid grid-cols-3 border-t border-slate-200">
            {[
              ['TBW', inbodyMeasurement.TBW],
              ['Lean mass', inbodyMeasurement.FFM],
              ['Weight', inbodyMeasurement.WT],
            ].map(([label, value]) => (
              <div key={label} className="border-l border-slate-200 px-4 py-3 first:border-l-0">
                <dt className="text-xs text-slate-500">{label}</dt>
                <dd className="mt-0.5 text-lg font-semibold text-slate-900">{formatNumberSmart(value)}</dd>
              </div>
            ))}
          </dl>
        </Card>

        <Card className="overflow-hidden">
          {panelTitle('Segmental analysis')}
          <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2">
            {[
              ['Right arm', inbodyMeasurement.LRA],
              ['Left arm', inbodyMeasurement.LLA],
              ['Trunk', inbodyMeasurement.LT],
              ['Right leg', inbodyMeasurement.LRL],
              ['Left leg', inbodyMeasurement.LLL],
            ].map(([label, value]) => (
              <div key={label} className="rounded-[10px] border border-slate-200 p-3">
                <p className="text-xs text-slate-500">{label}</p>
                <p className="mt-0.5 text-xl font-semibold text-slate-900">
                  {formatNumberSmart(value)} <span className="text-xs font-normal text-slate-500">lbs</span>
                </p>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Meters */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {meters.map((group) => (
          <Card key={group.label} className="p-4">
            <h3 className="text-[15px] font-semibold text-slate-900">{group.label}</h3>
            <div className="mb-2 mt-3 flex items-baseline justify-between">
              <span className="text-xs text-slate-500">{group.metric}</span>
              <span className="text-xl font-semibold text-slate-900">{formatNumberSmart(group.value)}</span>
            </div>
            <Progress
              value={group.target ? clampPercent((group.value / group.target) * 100) : clampPercent(((group.value - group.range[0]) / (group.range[1] - group.range[0])) * 100)}
              aria-label={group.label}
            />
            <p className="mt-3 text-xs text-slate-500">
              {group.metric === 'ECW/TBW'
                ? 'Current ratio shows optimal cellular hydration balance.'
                : `Your ${group.metric} is within the target performance range.`}
            </p>
          </Card>
        ))}
      </div>

      {/* Timeline snapshot */}
      <Card className="overflow-hidden">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 className="text-[15px] font-semibold text-slate-900">Performance timeline</h2>
          <button type="button" className="inline-flex items-center text-[13px] font-medium text-primary hover:text-primary/80">
            View all history
            <ChevronRight className="ml-0.5 h-3.5 w-3.5" />
          </button>
        </div>
        <dl className="flex flex-wrap gap-x-10 gap-y-4 p-4">
          {[
            ['Weight', inbodyMeasurement.WT, 'lbs'],
            ['Muscle mass', inbodyMeasurement.SMM, 'lbs'],
            ['Body fat %', inbodyMeasurement.PBF, '%'],
            ['Hydration', inbodyMeasurement.WED, ''],
            ['BMR', inbodyMeasurement.BMR, ''],
          ].map(([label, value, unit]) => (
            <div key={label}>
              <dt className="text-xs text-slate-500">{label}</dt>
              <dd className="mt-0.5 text-xl font-semibold text-slate-900">
                {formatNumberSmart(value)} <span className="text-xs font-normal text-slate-500">{unit}</span>
              </dd>
            </div>
          ))}
        </dl>
      </Card>

      {/* Profile */}
      <Card className="overflow-hidden">
        {panelTitle('Physiological profile')}
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4 p-4 md:grid-cols-3 lg:grid-cols-6">
          {[
            ['Full name', inbodyMeasurement.NAME],
            ['Height', inbodyMeasurement.HT],
            ['Age', inbodyMeasurement.AGE],
            ['Gender', inbodyMeasurement.GENDER],
            ['Visceral fat', inbodyMeasurement.VFALevel],
            ['BMR', inbodyMeasurement.BMR],
          ].map(([label, value]) => (
            <div key={label}>
              <dt className="text-xs text-slate-500">{label}</dt>
              <dd className="mt-0.5 text-sm font-medium text-slate-900">{value}</dd>
            </div>
          ))}
        </dl>
      </Card>
    </div>
  )
}

export default ClientResources
